import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ExtrasClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { addFieldsInfo, deletePostedLedgerAction, Ledger, updateProcessing } from '@store/calculations/ledgerSlice';

export interface State {
    isLoading: boolean;
    error: boolean;
    extrasList: Ledger[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    extrasList: [],
};

const extrasSlice = createSlice({
    name: 'extras',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getExtrasSuccess: (state, action: PayloadAction<Ledger[]>) => {
            state.extrasList = action.payload;
            state.isLoading = false;
        },
        postExtrasSuccess: (state) => {
            toast.success(stringResources.REDUX_SLICE.POST_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default extrasSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = extrasSlice.actions;

// for useSelector
export const selectExtras = (state: RootState) => state.extras as State;

// ----------------------------------------------------------------------
export function getExtras(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(extrasSlice.actions.startLoading());
        try {
            const client = new ExtrasClient(undefined, axios);
            const response = await client.getEstimatedExtras(fishingTripNumber);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(extrasSlice.actions.getExtrasSuccess(payload));
        } catch (error) {
            dispatch(extrasSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function postExtras(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(extrasSlice.actions.startLoading());
        try {
            const client = new ExtrasClient(undefined, axios);
            await client.postExtras(fishingTripNumber);
            dispatch(extrasSlice.actions.postExtrasSuccess());
        } catch (error) {
            dispatch(extrasSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deletePostedExtrasLedger(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(updateProcessing(true));
        const toastId = toast.loading(stringResources.REDUX_SLICE.DELETE_LOADING);
        try {
            const client = new ExtrasClient(undefined, axios);
            await client.deleteLedgerLines(exportId);
            dispatch(deletePostedLedgerAction(exportId));
            toast.update(toastId, {
                render: stringResources.REDUX_SLICE.DELETE_SUCCESS,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error) {
            dispatch(extrasSlice.actions.hasError(true));
            handleError(error, toastId);
        }
    };
}